<template>
  <div class="privacy-requests">
    <!-- Cards do topo: o que precisa de atenção agora -->
    <div class="stats-row">
      <div class="stat-card">
        <span class="stat-value">{{ stats.abertas }}</span>
        <span class="stat-label">Abertas</span>
      </div>
      <div class="stat-card" :class="{ 'stat-card--danger': stats.vencidas > 0 }">
        <span class="stat-value">{{ stats.vencidas }}</span>
        <span class="stat-label">Vencidas</span>
      </div>
      <div class="stat-card" :class="{ 'stat-card--warning': stats.vencendo > 0 }">
        <span class="stat-value">{{ stats.vencendo }}</span>
        <span class="stat-label">Vencendo em {{ warningDays }} dias</span>
      </div>
      <div class="stat-card">
        <span class="stat-value">{{ stats.acessos_30d }}</span>
        <span class="stat-label">Acessos a dado sensível (30d)</span>
      </div>
    </div>

    <vx-card>
      <div class="filters-row">
        <vs-select v-model="filters.status" label="Situação" class="filter-field" @input="load">
          <vs-select-item :value="''" text="Todas" />
          <vs-select-item value="PE" text="Pendente" />
          <vs-select-item value="EP" text="Em processamento" />
          <vs-select-item value="CO" text="Concluída" />
          <vs-select-item value="RJ" text="Recusada" />
        </vs-select>

        <vs-select v-model="filters.type" label="Tipo" class="filter-field" @input="load">
          <vs-select-item :value="''" text="Todos" />
          <vs-select-item v-for="(label, key) in typeLabels" :key="key" :value="key" :text="label" />
        </vs-select>

        <vs-checkbox v-model="filters.only_open" class="filter-check" @input="load">
          Só as abertas
        </vs-checkbox>
      </div>

      <div v-if="loading" class="state-message">Carregando…</div>

      <div v-else-if="!rows.length" class="state-message">
        Nenhuma requisição com estes filtros.
      </div>

      <table v-else class="privacy-table">
        <thead>
          <tr>
            <th>Protocolo</th>
            <th>Titular</th>
            <th>Tipo</th>
            <th>Situação</th>
            <th>Prazo (Art. 19)</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.id" :class="{ 'row-overdue': row.is_overdue }">
            <td>#{{ row.id }}</td>
            <td>
              <div class="subject">
                <strong>{{ row.user ? row.user.name : '—' }}</strong>
                <span>{{ row.user ? row.user.email : '' }}</span>
              </div>
            </td>
            <td>{{ row.type_label }}</td>
            <td>
              <span class="pill" :class="statusClass(row.status)">{{ row.status_label }}</span>
            </td>
            <td>
              <span :class="deadlineClass(row)">{{ deadlineLabel(row) }}</span>
            </td>
            <td class="actions-cell">
              <vs-button size="small" type="border" @click="openDetail(row.id)">Abrir</vs-button>
            </td>
          </tr>
        </tbody>
      </table>
    </vx-card>

    <!-- Detalhe -->
    <vs-popup :active.sync="detailOpen" title="Requisição do titular" class="privacy-popup">
      <div v-if="detail" class="detail">
        <div class="detail-head">
          <div>
            <h4>#{{ detail.id }} · {{ detail.type_label }}</h4>
            <p class="detail-subject">
              {{ detail.user ? detail.user.name : '—' }}
              <span>{{ detail.user ? detail.user.email : '' }}</span>
            </p>
          </div>
          <span class="pill" :class="statusClass(detail.status)">{{ detail.status_label }}</span>
        </div>

        <div class="detail-grid">
          <div><label>Aberta em</label><span>{{ formatDate(detail.requested_at) }}</span></div>
          <div><label>Prazo legal</label><span :class="deadlineClass(detail)">{{ formatDate(detail.due_at) }}</span></div>
          <div v-if="detail.handled_at"><label>Atendida em</label><span>{{ formatDate(detail.handled_at) }}</span></div>
          <div v-if="detail.handler"><label>Responsável</label><span>{{ detail.handler.name }}</span></div>
        </div>

        <div v-if="detail.description" class="detail-block">
          <label>O que o titular escreveu</label>
          <p>{{ detail.description }}</p>
        </div>

        <!--
          Eliminação é irreversível e não apaga tudo: pedido e pagamento ficam por
          obrigação fiscal. Quem clica precisa saber disso antes, não depois.
        -->
        <div v-if="isErasure(detail)" class="detail-warning">
          <strong>Atenção — ação irreversível.</strong>
          Concluir esta requisição anonimiza a conta: nome, e-mail, CPF, telefone,
          endereço e foto são apagados, cartões removidos e sessões encerradas.
          Pedidos e pagamentos <strong>permanecem</strong>, sem vínculo com o titular,
          por obrigação legal (5 anos).
        </div>

        <div v-if="detail.resolution_note" class="detail-block">
          <label>Resolução</label>
          <p>{{ detail.resolution_note }}</p>
        </div>

        <div class="detail-block">
          <label>Linha do tempo</label>
          <ul class="timeline">
            <li v-for="event in detail.events" :key="event.id">
              <span class="timeline-when">{{ formatDateTime(event.created_at) }}</span>
              <span class="timeline-what">
                <strong>{{ eventLabel(event.event) }}</strong>
                <template v-if="event.note"> — {{ event.note }}</template>
                <template v-if="event.user"> · {{ event.user.name }}</template>
              </span>
            </li>
          </ul>
        </div>

        <div v-if="isOpen(detail)" class="detail-actions">
          <vs-input
            v-model="resolutionNote"
            class="w-full mb-4"
            label="Observação (obrigatória para recusar)"
            placeholder="Descreva o que foi feito ou o motivo da recusa"
          />
          <div class="action-buttons">
            <vs-button
              v-if="detail.status === 'PE'"
              type="border"
              :disabled="working"
              @click="startProcessing"
            >
              Marcar em processamento
            </vs-button>
            <vs-button color="danger" type="border" :disabled="working" @click="reject">
              Recusar
            </vs-button>
            <vs-button :color="isErasure(detail) ? 'danger' : 'primary'" :disabled="working" @click="complete">
              {{ isErasure(detail) ? 'Executar anonimização' : 'Concluir' }}
            </vs-button>
          </div>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import PrivacyService from '@/services/api/PrivacyService'

/**
 * Fila de requisições do titular (LGPD, Art. 18).
 *
 * Ordenada por prazo, não por data de abertura: o que importa na tela é o que
 * vence antes. O Art. 19 dá 15 dias para responder, e alerta que só aparece
 * depois de estourado não serve para nada — daí a coluna de prazo em destaque e
 * a linha inteira em vermelho quando venceu.
 */
export default {
  name: 'PrivacyRequestsIndex',
  data() {
    return {
      loading: true,
      working: false,
      rows: [],
      stats: { abertas: 0, vencidas: 0, vencendo: 0, acessos_30d: 0 },
      warningDays: 3,
      filters: { status: '', type: '', only_open: true },
      detailOpen: false,
      detail: null,
      resolutionNote: '',
      typeLabels: {
        AC: 'Acesso aos dados',
        CO: 'Correção de dados',
        PO: 'Portabilidade',
        EL: 'Eliminação da conta',
        AN: 'Anonimização',
        IN: 'Informação sobre compartilhamento',
        RC: 'Revogação de consentimento'
      }
    }
  },
  mounted() {
    this.loadStats()
    this.load()
  },
  methods: {
    service() {
      return PrivacyService.build(this.$vs)
    },
    async loadStats() {
      try {
        this.stats = await this.service().dashboard()
      } catch (e) {
        /* os cards ficam zerados; a fila abaixo é o que importa */
      }
    },
    async load() {
      this.loading = true
      try {
        const response = await this.service().requestsGrid({
          status: this.filters.status,
          type: this.filters.type,
          only_open: this.filters.only_open ? 1 : ''
        })

        this.rows = response.data || []
      } catch (e) {
        this.rows = []
      } finally {
        this.loading = false
      }
    },
    async openDetail(id) {
      this.resolutionNote = ''
      try {
        this.detail = await this.service().requestDetail(id)
        this.detailOpen = true
      } catch (e) {
        /* o serviço já notifica */
      }
    },
    async startProcessing() {
      this.working = true
      try {
        this.detail = await this.service().startProcessing(this.detail.id)
        this.afterChange('Requisição marcada como em processamento.')
      } finally {
        this.working = false
      }
    },
    async complete() {
      // Anonimização não se desfaz — a confirmação extra existe por isso.
      if (this.isErasure(this.detail)) {
        const ok = window.confirm(
          'Confirma a anonimização definitiva da conta deste titular? Esta ação não pode ser desfeita.'
        )
        if (!ok) return
      }

      this.working = true
      try {
        this.detail = await this.service().complete(this.detail.id, this.resolutionNote)
        this.afterChange('Requisição concluída.')
      } finally {
        this.working = false
      }
    },
    async reject() {
      if (!this.resolutionNote) {
        this.notify('Escreva o motivo da recusa — recusa sem justificativa é indefensável perante a ANPD.', 'warning')
        return
      }

      this.working = true
      try {
        this.detail = await this.service().reject(this.detail.id, this.resolutionNote)
        this.afterChange('Requisição recusada.')
      } finally {
        this.working = false
      }
    },
    afterChange(message) {
      this.notify(message, 'success')
      this.detailOpen = false
      this.loadStats()
      this.load()
    },
    isOpen(request) {
      return ['PE', 'EP'].includes(request.status)
    },
    isErasure(request) {
      return ['EL', 'AN'].includes(request.type)
    },
    statusClass(status) {
      return { PE: 'pending', EP: 'processing', CO: 'done', RJ: 'rejected' }[status] || 'pending'
    },
    deadlineClass(row) {
      if (!this.isOpen(row)) return ''
      if (row.is_overdue) return 'deadline-overdue'
      if (row.days_to_due !== null && row.days_to_due <= this.warningDays) return 'deadline-warning'
      return ''
    },
    deadlineLabel(row) {
      if (!this.isOpen(row)) return this.formatDate(row.due_at)
      if (row.is_overdue) return `Vencida (${this.formatDate(row.due_at)})`
      if (row.days_to_due === 0) return 'Vence hoje'
      return `${row.days_to_due} dia(s) · ${this.formatDate(row.due_at)}`
    },
    eventLabel(event) {
      return {
        created: 'Aberta pelo titular',
        processing: 'Em processamento',
        exported: 'Arquivo gerado',
        anonymized: 'Dados anonimizados',
        completed: 'Concluída',
        rejected: 'Recusada'
      }[event] || event
    },
    formatDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleDateString('pt-BR')
    },
    formatDateTime(value) {
      if (!value) return '—'
      return new Date(value).toLocaleString('pt-BR')
    },
    notify(text, color) {
      this.$vs.notify({ title: 'Privacidade', text, color, iconPack: 'feather', icon: 'icon-shield' })
    }
  }
}
</script>

<style lang="scss" scoped>
.stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  background: #fff;
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  &--danger .stat-value { color: #ea5455; }
  &--warning .stat-value { color: #ff9f43; }
}

.stat-value {
  font-size: 1.6rem;
  font-weight: 600;
  line-height: 1;
}

.stat-label {
  font-size: 0.78rem;
  color: #6e6b7b;
}

.filters-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.filter-field {
  min-width: 200px;
}

.filter-check {
  margin-bottom: 0.5rem;
}

.state-message {
  padding: 2rem 0;
  text-align: center;
  color: #6e6b7b;
}

.privacy-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  th {
    text-align: left;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #6e6b7b;
    padding: 0.5rem;
    border-bottom: 1px solid #ebe9f1;
  }

  td {
    padding: 0.75rem 0.5rem;
    border-bottom: 1px solid #f5f4f7;
    vertical-align: middle;
  }
}

.row-overdue {
  background: #fff5f5;
}

.subject {
  display: flex;
  flex-direction: column;

  span {
    font-size: 0.75rem;
    color: #6e6b7b;
  }
}

.pill {
  display: inline-block;
  padding: 0.15rem 0.65rem;
  border-radius: 1rem;
  font-size: 0.72rem;
  font-weight: 600;

  &.pending { background: #fff3e0; color: #b26a00; }
  &.processing { background: #e3f2fd; color: #1565c0; }
  &.done { background: #e8f5e9; color: #2e7d32; }
  &.rejected { background: #ffebee; color: #c62828; }
}

.deadline-overdue {
  color: #ea5455;
  font-weight: 600;
}

.deadline-warning {
  color: #ff9f43;
  font-weight: 600;
}

.actions-cell {
  white-space: nowrap;
}

/* Detalhe */
.detail-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;

  h4 { margin: 0 0 0.25rem; }
}

.detail-subject {
  margin: 0;
  font-size: 0.85rem;

  span {
    display: block;
    color: #6e6b7b;
    font-size: 0.78rem;
  }
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.75rem;
  padding: 0.75rem 0;
  border-top: 1px solid #ebe9f1;
  border-bottom: 1px solid #ebe9f1;
  margin-bottom: 1rem;

  label {
    display: block;
    font-size: 0.7rem;
    text-transform: uppercase;
    color: #6e6b7b;
    margin-bottom: 0.15rem;
  }

  span {
    font-size: 0.85rem;
  }
}

.detail-block {
  margin-bottom: 1rem;

  label {
    display: block;
    font-size: 0.7rem;
    text-transform: uppercase;
    color: #6e6b7b;
    margin-bottom: 0.35rem;
  }

  p {
    margin: 0;
    font-size: 0.875rem;
  }
}

.detail-warning {
  background: #fff5f5;
  border-left: 3px solid #ea5455;
  padding: 0.75rem 1rem;
  border-radius: 0.35rem;
  font-size: 0.82rem;
  line-height: 1.5;
  margin-bottom: 1rem;
}

.timeline {
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    display: flex;
    gap: 0.75rem;
    padding: 0.35rem 0;
    font-size: 0.82rem;
    border-bottom: 1px dashed #f0eef4;
  }
}

.timeline-when {
  flex-shrink: 0;
  color: #6e6b7b;
  min-width: 140px;
}

.detail-actions {
  border-top: 1px solid #ebe9f1;
  padding-top: 1rem;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: flex-end;
}
</style>
